import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/features/auth/session';
import { useMyGuardianStudents } from '@/features/guardian/hooks';
import { OrganizationProgressCard } from '@/features/memorization/organization-progress-card';
import { PendingEvaluationsCard } from '@/features/memorization/pending-evaluations-card';
import { QuranProgressMap } from '@/features/memorization/quran-progress-map';
import { roleLabel } from '@/features/membership/labels';
import { MurajaahQueueSummaryCard } from '@/features/murajaah/queue-summary-card';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import { JoinPondokCard } from '@/features/organization/join-pondok-card';
import { NoMembershipPanel } from '@/features/organization/no-membership-panel';
import { useIsPersonalSpace } from '@/features/personal-space/hooks';
import { PersonalSpaceCard } from '@/features/personal-space/personal-space-card';
import { MyStudentSummaryCard } from '@/features/students/my-student-summary-card';
import { useMyStudentProfile, useStudents } from '@/features/students/hooks';
import { WelcomeHero } from '@/features/welcome/welcome-hero';
import { useHasCapability } from '@/lib/capabilities';

/**
 * The authenticated landing page. Phase G-1 built the quick-navigation
 * cards (Students, Anak Saya); Phase G-3 added the Teacher Workspace
 * surfaces; Phase G-4 added the Student's own "Ringkasan Saya" summary -
 * every card renders nothing for a caller it doesn't apply to, so a role
 * only ever sees what applies to it.
 *
 * Two additions here address a real reported problem: an account holding
 * no organization membership used to land on a page where every one of
 * those cards self-hid, leaving an unexplained near-blank screen (the
 * expected state for an invitation-only product, but indistinguishable
 * from a broken one). It now gets `NoMembershipPanel` instead. A Student
 * who does belong somewhere gets `QuranProgressMap`, so their own landing
 * page shows their standing across the mushaf rather than two counters.
 *
 * The header is `WelcomeHero` (Bismillah and a short motivation on
 * reading, memorizing, and living the Qur'an), and the no-membership
 * state now leads with starting alone rather than waiting for an
 * invitation (ADR-031).
 */
export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const { memberships, activeMembership } = useActiveOrganization();
  const canManageStudents = useHasCapability('students:manage');
  const { data: students } = useStudents(1, 1);
  const { data: myChildren } = useMyGuardianStudents();
  const { data: myStudentProfile } = useMyStudentProfile(!canManageStudents);

  const hasNoMembership = memberships.length === 0;
  const inPersonalSpace = useIsPersonalSpace();

  const subtitleText = hasNoMembership
    ? 'Mulai sendiri, atau bergabung ke pondok untuk dibimbing seorang ustadz'
    : inPersonalSpace
      ? 'Ruang hafalan pribadi Anda - Anda yang menentukan targetnya'
      : 'Platform hafalan Qur’an untuk pondok & rumah tahfidz';

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 sm:p-6">
      <WelcomeHero
        fullName={user?.fullName ?? null}
        subtitle={
          <>
            {activeMembership !== null && (
              <Badge variant="secondary">{roleLabel(activeMembership.roleName)}</Badge>
            )}
            <span>{subtitleText}</span>
          </>
        }
      />

      {hasNoMembership ? (
        <NoMembershipPanel />
      ) : (
        <>
          <MyStudentSummaryCard />

          {myStudentProfile !== null && myStudentProfile !== undefined && (
            <>
              <PersonalSpaceCard studentId={myStudentProfile.id} />
              <QuranProgressMap studentId={myStudentProfile.id} />
            </>
          )}

          <OrganizationProgressCard />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* A personal space has exactly one student - the person
                reading this - so "1 santri" is noise there, not a roster. */}
            {!inPersonalSpace && (
              <Link to="/students">
                <Card className="h-full transition-colors hover:bg-secondary/50">
                  <CardHeader>
                    <CardTitle className="text-base">Santri</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    {students !== undefined ? `${students.total} santri` : 'Lihat daftar santri'}
                  </CardContent>
                </Card>
              </Link>
            )}

            {myChildren !== undefined && myChildren.length > 0 && (
              <Link to="/guardians/me">
                <Card className="h-full transition-colors hover:bg-secondary/50">
                  <CardHeader>
                    <CardTitle className="text-base">Anak Saya</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    {myChildren.length} anak terhubung
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>

          <PendingEvaluationsCard />

          <MurajaahQueueSummaryCard />

          {/* Starting alone used to erase this: NoMembershipPanel holds the
              only explanation of how to join a pondok, and it renders only
              for an account that belongs to nothing. ADR-031's premise is
              the opposite - a pondok is additive, and the personal space
              survives joining - so the path has to stay reachable. */}
          {inPersonalSpace && <JoinPondokCard showsWhatIsWaiting />}
        </>
      )}
    </div>
  );
}
